<template lang="pug">
  .sidecart
    transition(name="fade")
      .sidecart__overlay(v-if="sidecartOpen", @click="$store.dispatch('cart/closeSidecart')")
    transition(name="off-canvas-right")
      .sidecart__drawer(v-if="sidecartOpen")
        .sidecart__header
          .sidecart__header-content
            .sidecart__count {{ cartCountText }}
            h2.sidecart__title(
              ref="sidecartHeading"
              :tabindex="0"
            ) {{ heading }}
            base-button.button--icon.sidecart__close(
              @click="$store.dispatch('cart/closeSidecart')"
            )
              .u-sr-only Close side cart
              icon.button__icon(
                name="close"
                size="20px"
              )
        .sidecart__container
          .sidecart__contents-wrapper(v-if="cartData.item_count > 0", key="content")
            .sidecart__contents
              cart-line-item(
                v-for="(item, index) in cartData.items"
                :item="item"
                :line="index + 1"
                :key="item.id"
              )
            .sidecart__footer
              cart-totals-table
              base-button.button--full-width.sidecart__checkout-cta(
                :full="true"
                :href="checkoutURL"
              ) Checkout
              .sidecart__promo(
                v-if="promoMessage"
                v-html="promoMessage"
              )
          .sidecart__empty(
            v-else
            key="empty"
          )
            p.sidecart__empty-text {{ emptyText }}
            base-button.sidecart__empty-cta(
              v-if="emptyCartCtaUrl && emptyCartCtaText"
              :href="emptyCartCtaUrl"
            ) {{ emptyCartCtaText }}
            base-button.sidecart__empty-cta.sidecart__empty-cta--secondary.button--green(
              v-if="emptyCartSecondaryCtaUrl && emptyCartSecondaryCtaText"
              :href="emptyCartSecondaryCtaUrl"
            ) {{ emptyCartSecondaryCtaText }}
</template>

<script>
  import { mapState } from 'vuex';
  import { getRechargeURL } from 'scripts/helpers/recharge.js';

  import Icon from 'scripts/components/basic/Icon.vue';
  import BaseButton from 'scripts/components/buttons/BaseButton.vue';
  import CartLineItem from 'scripts/components/cart/CartLineItem.vue';
  import CartTotalsTable from 'scripts/components/cart/CartTotalsTable.vue';

  export default {
    name: 'Sidecart',
    components: { BaseButton, Icon, CartLineItem, CartTotalsTable },
    props: {
      heading: String,
      emptyText: String,
      emptyCartCtaText: String,
      emptyCartCtaUrl: String,
      emptyCartSecondaryCtaText: String,
      emptyCartSecondaryCtaUrl: String,
      promoMessage: String,
      emptyImage: String,
      emptyImageAlt: String,
      headerBackgroundImage: String,
      customerId: String,
      customerEmail: String,
      permanentDomain: String,
    },
    computed: {
      ...mapState({
        cartData: (state) => state.cart,
        items: (state) => state.cart.items,
        allProducts: (state) => state.products.all,
        sidecartOpen: (state) => state.cart.sidecartOpen,
      }),
      cartCountText() {
        return this.cartData.item_count === 1 ? '1 item' : `${this.cartData.item_count} items`;
      },
      hasSubscriptionProducts() {
        return !!this.items.find((product) => product.properties && product.properties.subscription_id);
      },
      checkoutURL() {
        return this.hasSubscriptionProducts ? getRechargeURL(this.permanentDomain, this.customerId, this.customerEmail) : '/checkout';
      },
    },
    watch: {
      totalPrice() {
        this.checkGiftWithPurchase();
      },
      sidecartOpen() {
        this.$nextTick(() => {
          if (this.sidecartOpen) {
            this.$refs.sidecartHeading.focus();
          }
        });
      },
    },
  };
</script>

<style scoped lang="scss">
  /*
   * 1. Override auto to prevent growing too tall
   *
   */

  .sidecart {
  }

  .sidecart__overlay {
    position: fixed;
    top: 0;
    right: 0;
    z-index: index($layout, 'sidecart-overlay');
    width: 100%;
    height: 100%;
    background-color: $color-transparent-black;
  }

  .sidecart__drawer {
    display: flex;
    position: fixed;
    top: 0;
    right: 0;
    z-index: index($layout, 'sidecart-drawer');
    width: 100%;
    height: 100%;
    flex-direction: column;
    background-color: $bg--main;

    @include tablet-up {
      max-width: 480px;
    }
  }

  .sidecart__header {
    position: relative;
    flex-shrink: 0;
  }

  .sidecart__header-background {
    padding-bottom: 27.5%;
    height: 0;
    background-size: 105%;
    background-repeat: no-repeat;
    background-position: top left;
  }

  .sidecart__header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ($spacing-unit * 4);
    width: 100%;
  }

  .sidecart__title {
    position: absolute;
    right: 0;
    left: 0;
    margin: 0;
    font-size: rem(32);
    line-height: rem(40);
    text-align: center;

    @include tablet-up {
      font-size: rem(40);
    }

    &:focus {
      box-shadow: none;
    }
  }

  .sidecart__count {
    font-family: $font-secondary;
    font-size: rem(14);
    line-height: rem(20);

    @include tablet-up {
      font-size: rem(16);
    }
  }

  .sidecart__container {
    display: flex;
    flex-grow: 1;
    min-height: 0; /* [1] */
  }

  .sidecart__contents-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-grow: 1;
  }

  .sidecart__contents {
    flex-grow: 1;
    padding: ($spacing-unit * 5) ($spacing-unit * 4);
    overflow-y: auto;
    overflow-x: hidden;
  }

  .sidecart__checkout-cta {
    margin-top: 10px;
  }

  .sidecart__empty {
    display: flex;
    padding: ($spacing-unit * 10) 0;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    background-size: 181px;
    background-repeat: no-repeat;
    background-position: bottom right;

    @include tablet-up {
      background-size: 251px;
    }
  }

  .sidecart__empty-text {
    margin-bottom: $spacing-unit * 10;
    color: $color-black;
    font-size: rem(18);
    line-height: rem(20);
    letter-spacing: rem(1.13);
    text-transform: uppercase;
  }

  .sidecart__empty-cta {
    margin-bottom: $spacing-unit * 4;
    padding: ($spacing-unit * 3) ($spacing-unit * 5);
    width: 250px;
    white-space: normal;
  }

  .sidecart__empty-cta--secondary {
  }

  .sidecart__footer {
    padding: $grid-gutter ($spacing-unit * 4);
    padding-top: 8px;
    flex-shrink: 0;
  }

  .sidecart__promo {
    padding: ($spacing-unit * 4) 0;

    &::v-deep p {
      margin: 0;
      padding: 0;
      font-size: rem(14);
      line-height: rem(20);
      text-align: center;
    }
  }
</style>
